import React from "react";
import { Link, useHistory } from "react-router-dom";

import moment from "moment";

import type { Event } from "~/calendar/CalendarPage";
import { AppContext, GlobalKeyboardListener } from "~/common";
import Attendees, { type AttendeeAttrs, type AttendeesPresenceInfo } from "~/common/Attendees";
import { ProseReact } from "~/lib/prose";
import { MultiCursor } from "~/lib/prose/plugins";
import type { UserPresence } from "~/lib/prose/plugins/multicursor";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { nGetText } from "~/lib/utils";
import { useLongestMeetingRecording } from "~/meetingRecorder/utils";
import { getTemplateContentEditorConfig, NoteTemplateContent } from "~/noteTemplate/content";
import { getPublicEditorConfig } from "~/public/Note";
import { Button } from "~/ui";

import type { EventExtraData, RowProps } from "./index";
import RecordingBanner from "./RecordingBanner";

import type { ViewEventDetailQuery } from "~/__generated__/ViewEventDetailQuery.graphql";

import styles from "./ViewEvent.module.css";

type NoteProps = RowProps & {
	viewer: ViewEventDetailQuery["response"]["viewer"];
};

export default function Note({ event, eventData, viewer }: NoteProps) {
	const { user } = React.useContext(AppContext);
	const [attendeesPresenceInfo, setAttendeesPresenceInfo] = React.useState<AttendeesPresenceInfo>({});
	const history = useHistory();

	const onUserPresenceChange = React.useCallback((presence: UserPresence) => {
		const { sessionId, user, lastActive, isCurrentlyViewing, platform } = presence;
		setAttendeesPresenceInfo(prev => {
			const userSessions = prev[user.id];
			const newUserSessions = [
				{
					sessionId,
					isCurrentlyViewing: !!isCurrentlyViewing,
					lastActiveDatetime: lastActive ? moment(lastActive).format() : undefined,
					platform: platform,
				},
				...(userSessions ?? []).filter(s => s.sessionId !== sessionId),
			];

			return {
				...prev,
				[user.id]: newUserSessions,
			};
		});
	}, []);

	const note = eventData.note;

	const multiCursorRef = React.useRef<MultiCursor | null>(null);
	const editorConfig = React.useMemo(() => {
		if (!note) {
			return null;
		}

		const noteUsers = (note?.noteuserSet || []).filter(Boolean).map(nu => nu.user);
		const participants = [...noteUsers, ...(note?.externalUsers || [])];
		const updatedNote = {
			...note,
			participants,
			eventStart: note.eventStart || undefined,
			createdAt: note.createdAt || "",
			updatedAt: note.updatedAt || undefined,
			attachments: [],
		};

		const config = getPublicEditorConfig(
			user?.id,
			viewer.account.id,
			updatedNote,
			undefined,
			undefined,
			false,
			onUserPresenceChange,
		);
		multiCursorRef.current = config.multicursor;

		return config.config;
	}, [note, user, viewer.account.id, onUserPresenceChange]);

	React.useEffect(() => {
		multiCursorRef.current?.onScreenVisible();

		return () => multiCursorRef.current?.onScreenHidden();
	}, []);

	const viewNoteClicked = React.useCallback(() => {
		dispatchAnalyticsTrack("View event action", { feature: "Calendar", action: "view note" });
	}, []);

	const viewNote = React.useCallback(
		(url: string) => () => {
			dispatchAnalyticsTrack("Keyboard shortcut used: view notes", { feature: "Calendar", action: "view note" });
			history.push(url);
		},
		[history],
	);

	const noteContentClicked = React.useCallback(() => {
		dispatchAnalyticsTrack("View event action", { feature: "Calendar", action: "note clicked" });
	}, []);

	const longestMeetingRecording = useLongestMeetingRecording(note?.meetingRecordings || null);

	if (!note || !editorConfig) {
		if (eventData.stream?.template) {
			return <TemplateView event={event} eventData={eventData} />;
		}

		if (viewer.account.defaultOneononeTemplate && eventData.isOneOnOne) {
			return <TemplateView event={event} eventData={eventData} template={viewer.account.defaultOneononeTemplate} />;
		}

		if (viewer.account.defaultMeetingTemplate) {
			return <TemplateView event={event} eventData={eventData} template={viewer.account.defaultMeetingTemplate} />;
		}

		if (viewer.account.defaultBasicTemplate) {
			return <TemplateView event={event} eventData={eventData} template={viewer.account.defaultBasicTemplate} />;
		}

		return <TemplateViewWrapper event={event} />;
	}

	const noteUsers = (note?.noteuserSet || []).filter(Boolean).map(nu => ({ ...nu, ...nu.user, invitable: false }));
	const externalUsers = (note?.externalUsers || []).map(eu => ({ ...eu, user: eu, invitable: false }));
	const participants = [...noteUsers, ...externalUsers];

	let notePreview = (
		<article>
			<header className={styles.noteHeader}>
				<div>
					{note.url && (
						<Link to={note.url}>
							<GlobalKeyboardListener sequence="enter" onEvent={viewNote(note.url)} />
							<Button size="slim" variant="primary" onClick={viewNoteClicked}>
								Open notes
							</Button>
						</Link>
					)}
				</div>

				<Attendees
					attendees={participants as AttendeeAttrs[]}
					presenceInfo={attendeesPresenceInfo}
					maxLength={10}
					avatarSize="20px"
					sizeInPixels={20}
				/>
			</header>

			{longestMeetingRecording && <RecordingBanner noteId={note.id} recording={longestMeetingRecording} />}

			<div className="editor" onClick={noteContentClicked}>
				{note.rawDraftJson && (
					<ProseReact
						enableReactHost
						editable={false}
						config={editorConfig}
						initialDoc={note.rawDraftJson.doc}
						context={{
							participants,
							noteId: note.id,
							noteUrl: note.url,
							noteEventGuid: event.guid,
						}}
					/>
				)}
			</div>
		</article>
	);

	if (note.url) {
		notePreview = (
			<Link to={note.url} onClick={viewNoteClicked}>
				{notePreview}
			</Link>
		);
	}

	return (
		<div className={styles.editorWrapper}>
			<div className="notes-stream-card small-note-card">
				<div className="main">{notePreview}</div>
			</div>
		</div>
	);
}

type TemplateViewProps = RowProps & {
	template?: NonNullable<EventExtraData["stream"]>["template"];
};

function TemplateViewWrapper({ event, children }: { event: Event; children?: React.ReactNode }) {
	return (
		<div className={styles.editorWrapper}>
			<div className="notes-stream-card small-note-card">
				<div className="main">
					<Link to={`/meetings/${event.guid}/`}>
						<article>
							<header className={styles.noteHeader}>
								<div>
									<Button size="slim" variant="primary">
										Open notes
									</Button>
								</div>
							</header>
							<div className="editor template">{children}</div>
						</article>
					</Link>
				</div>
			</div>
		</div>
	);
}

function TemplateView({ event, eventData, template }: TemplateViewProps) {
	const stream = eventData.stream;
	const templateToUse = template || stream?.template;

	const editorConfig = React.useMemo(
		() => getTemplateContentEditorConfig(templateToUse?.schemaVersion || 1, false, true),
		[templateToUse],
	);

	if (!templateToUse) {
		return null;
	}

	const previousNote = eventData.previousOccurrence?.note;

	return (
		<TemplateViewWrapper event={event}>
			<>
				{previousNote && stream && <CarriedForwardBanner stream={stream} previousNote={previousNote} />}

				<NoteTemplateContent
					document={templateToUse.proseDoc}
					editorConfig={editorConfig}
					hasAccessToPromptHeadings={false} // TODO
					editable={false}
				/>
			</>
		</TemplateViewWrapper>
	);
}

type CarriedForwardBannerProps = {
	stream: NonNullable<EventExtraData["stream"]>;
	previousNote: NonNullable<NonNullable<EventExtraData["previousOccurrence"]>["note"]>;
};

function CarriedForwardBanner({ stream, previousNote }: CarriedForwardBannerProps) {
	let incompleteTalkingPoints = null;
	let incompleteActionItems = null;
	let separator = "";

	if (stream.carryActionItems && previousNote.pendingAgendaCount) {
		incompleteTalkingPoints = (
			<span className={styles.boldTextPrimaryColor}>{`${previousNote.pendingAgendaCount} incomplete talking ${nGetText(
				previousNote.pendingAgendaCount,
				"point",
				"points",
			)}`}</span>
		);
	}

	if (stream.carryTalkingPoints && previousNote.pendingTodoCount) {
		incompleteActionItems = (
			<span className={styles.boldTextPrimaryColor}>{`${previousNote.pendingTodoCount} incomplete action ${nGetText(
				previousNote.pendingTodoCount,
				"item",
				"items",
			)}`}</span>
		);
	}

	if (incompleteTalkingPoints && incompleteActionItems) {
		separator = " and ";
	}

	if (!incompleteTalkingPoints && !incompleteActionItems) {
		return null;
	}

	return (
		<div className={styles.carriedBanner}>
			{incompleteTalkingPoints}
			{separator}
			{incompleteActionItems} will be carried-forward from the previous meeting.
		</div>
	);
}
